import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'lodash';
import moment from 'moment';
import classes from './script.module.scss';
import ScriptsStatusBar from '../ScriptsStatusBar/ScriptsStatusBar';
import Button from '../../../_common/Form/Buttons/Button';
import Tabs from '../../../_common/Tabs/Tabs';
import {
  getPatientScriptsRequest, getScriptStatusRequest, setScrollingToNoteId,
  updateScriptRequest,
  updateScriptStatusesRequest,
} from '../../../../store/actions/script';
import Notes from './Tabs/Notes';
import accountHelpers from '../../../../utils/accountHelpers';
import StatusForm from './StatusForm/StatusForm';
import RXHistory from './Tabs/RXHistory';
import Loader from '../../../_common/Loader/Loader';
import CancelModal from './CancelModal/CancelModal';
import StatusField from '../../../_common/StatusField/StatusField';
import Status from './Tabs/Status';
import Payments from './Tabs/Payments';
import Attachments from './Tabs/Attachments';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';
import api from '../../../../api/api';
import Details from './Tabs/Details';
import PaymentModal from './PaymentModal/PaymentModal';
import PaymentSuccessModal from './PaymentSuccessModal/PaymentSuccessModal';
import Checkbox from '../../../_common/Form/Checkbox/Checkbox';

const Script = () => {
  const userId = accountHelpers.getUser().id;
  const isAdmin = accountHelpers.isInOneOfRoles(['Admin', 'Pharmacy']);
  const isPatient = accountHelpers.isInOneOfRoles(['Patient']);
  const isHealth = accountHelpers.isInOneOfRoles(['Health']);

  const { scriptId } = useParams();
  const dispatch = useDispatch();
  const { scrollingToNoteId, scheduleDeliveryMethod, postalServicesData } = useSelector((store) => store.script);

  const [scriptLoading, setScriptLoading] = useState(true);

  const [assignBtnText, setAssignBtnText] = useState('');
  const [assignToLoading, assignToLoadingToggle] = useState(false);

  const [currentTab, setCurrentTab] = useState('Details');

  const [scriptData, setScriptData] = useState({});
  const [tabCounts, setTabCounts] = useState({});
  const [isCancelModalOpen, cancelModalOpeningToggle] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [shippingLoad, setShippingLoad] = useState(false);

  const [refillModalInfo, setRefillModalInfo] = useState({});
  const [refillSaveLoading, setRefillSaveLoading] = useState(false);
  const [isPaymentModalOpen, paymentModalToggle] = useState(false);
  const [paymentSuccessModalData, setPaymentSuccessModalData] = useState({});

  const {
    status, PatientId, PhysicianId, ProductId, pouch, location, homeCare, processedOn, transLocation,
    transNPI, transDate, priorAuth, patientPay, copayApproval, copayNetwork, networkPay, Product,
    shipOn, deliveryMethod, trackNum, ETA, paymentOption, cancelReason, scheduleIds, quantity,
    refills, billOnDate, daysSupply, refillsRemaining, pharmNPI, salesCode, hcPhone, hcHome, assign,
    directions, secDiagnosis, secInsPay, diagnosis, rxNumber, primInsPay, cost, writtenDate, pharmDate, frequencyCode,
  } = scriptData;

  const bodyOverflow = useCallback(() => {
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('width');
    document.body.ontouchmove = () => true;
  }, [isPaymentModalOpen, paymentSuccessModalData]);

  useEffect(() => {
    if (!isPaymentModalOpen && _.isEmpty(paymentSuccessModalData)) {
      bodyOverflow();
    }
  }, [isPaymentModalOpen, paymentSuccessModalData]);

  useEffect(() => {
    (async () => {
      setScriptLoading(true);

      const { payload: { data } } = await dispatch(getPatientScriptsRequest({ userId, scriptId }));

      const script = data?.response?.[0];
      setAssignBtnText(data?.assignment);
      setScriptData({ ...script, deliveryMethod: script.homeCare ? 'US Postal' : script.deliveryMethod });

      setTabCounts({
        Notes: script.scriptNotes?.length || 0,
        Attachments: script.scriptAttachments?.length || 0,
        'RX History': data.rxHistoryCount || 0,
        Statuses: script.scriptStatuses?.length || 0,
        Payments: script.scriptPayments?.length || 0,
      });

      setScriptLoading(false);
    })();
  }, [scriptId]);

  useEffect(() => {
    if (scrollingToNoteId) {
      setCurrentTab('Notes');
    }
  }, [scrollingToNoteId]);

  const changeTabCounts = (name, number = 1) => {
    setTabCounts((prev) => ({ ...prev, [name]: +prev[name] + number }));
  };

  const changeTab = (tab) => {
    setCurrentTab(tab);

    if (scrollingToNoteId) {
      dispatch(setScrollingToNoteId(null));
    }
  };

  const changeStatus = async (changedData, info = {}) => {
    setUpdateLoading(true);

    const data = {
      id: +scriptId,
      userId,
      PatientId,
      PhysicianId,
      ProductId,
      status,
      pouch,
      location,
      homeCare,
      processedOn,
      transLocation,
      transNPI,
      transDate,
      priorAuth,
      patientPay,
      copayApproval,
      copayNetwork,
      networkPay,
      shipOn,
      deliveryMethod,
      trackNum,
      ETA,
      paymentOption,
      cancelReason,
      ...changedData,
    };

    // const sendData = utils.keysToCamelCase(data, ['ETA']);
    const sendData = data;

    const statusChangeData = {
      userId,
      scriptId,
      fromStatus: status,
      toStatus: changedData.status,
      manuallyChanged: false,
      fromProcessedOn: null,
      toProcessedOn: null,
    };

    if (scheduleIds) {
      sendData.scheduleIds = scheduleIds;

      // scheduleIds.forEach((id) => {
      //   dispatch(updateScriptStatusesRequest({
      //     ...statusChangeData,
      //     scriptId: id,
      //   }));
      // });
    }

    try {
      if (changedData.hasOwnProperty('pouch')) {
        dispatch(updateScriptRequest(sendData));
      } else {
        await Promise.all([
          dispatch(updateScriptRequest(sendData)),
          dispatch(updateScriptStatusesRequest(statusChangeData)),
        ]);

        toast.success(`Script status set to "${changedData.status}"`);

        changeTabCounts('Statuses');

        if (currentTab === 'Statuses') {
          dispatch(getScriptStatusRequest({ ScriptId: scriptId }));
          window.scrollTo(0, 0);
        }
      }

      setScriptData((prev) => ({
        ...prev, ...data, ...info, scheduleIds: null,
      }));
    } catch (e) {
      console.warn(e);
      toast.error('Status was not changed ERROR');
    }

    setUpdateLoading(false);
  };

  const changeScriptInfo = (info) => {
    setScriptData((prev) => ({ ...prev, ...info }));
  };

  const setRefillModalData = (addNewScript = false) => {
    const { doNotRefill } = scriptData;
    const productQuantity = +Product.quantity;
    const productName = Product.name;
    const { id } = Product;

    const newProductQuantity = Math.round((productQuantity - +quantity) * 100) / 100;

    const title = doNotRefill
      ? 'Update quantity?'
      : 'Generate refill?';

    const text = addNewScript
      ? <div>&nbsp;</div>

      : (doNotRefill
        ? `This will deduct ${quantity} from the on hand quantity of ${productName}\n
           ${productQuantity} - ${quantity} => ${newProductQuantity} (new on hand quantity)`

        : `This will deduct ${quantity} from the on hand quantity of ${productName}\n
           ${productQuantity} - ${quantity} => ${newProductQuantity} (new on hand quantity)`);

    setRefillModalInfo({
      id,
      productName,
      quantity,
      productQuantity,
      newProductQuantity,
      doNotRefill,
      title,
      text,
      addNewScript,
    });
  };

  const saveRefillStatus = async () => {
    setRefillSaveLoading(true);
    let newStatus;

    const {
      id, productName, newProductQuantity, doNotRefill, addNewScript,
    } = refillModalInfo;

    const data = {
      id,
      name: productName,
      quantity: newProductQuantity,
    };

    let refillsData;

    if (!doNotRefill || addNewScript) {
      if (addNewScript && !moment(billOnDate).isValid()) {
        toast.error('Invalid date - you are missing Bill On or Days Supply');

        setRefillSaveLoading(false);
        setRefillModalInfo({});
        return;
      }

      // const num = +refillsRemaining || 0;
      // newStatus = +num !== -1 ? 'Refill' : 'Renew';
      const daysSub = daysSupply >= 80 ? 24 : daysSupply > 34 ? 7 : 10;
      const newProcessedOn = moment(shipOn).add(daysSupply, 'days').subtract(daysSub, 'days').format('YYYY-MM-DD');
      const evRefillsRe = refillsRemaining - 1;
      const newRefills = evRefillsRe < -1 ? -1 : +refills + 1;
      const newRefillsRemaining = evRefillsRe < -1 ? -1 : evRefillsRe;
      newStatus = +newRefillsRemaining !== -1 ? 'Refill' : 'Renew';

      const sendData = {
        PatientId,
        PhysicianId,
        UserId: userId,
        processedOn: newProcessedOn || '',
        status: newStatus || '',
        refills: newRefills || 0,
        refillsRemaining: newRefillsRemaining || 0,
        pouch: pouch || false,
        pharmNPI: pharmNPI || null,
        priorAuth: priorAuth || '',
        location: location || '',
        pharmDate: pharmDate || '',
        writtenDate: writtenDate || '',
        billOnDate: billOnDate || '',
        cost: cost || null,
        rxNumber: rxNumber || null,
        primInsPay: primInsPay || null,
        diagnosis: diagnosis || '',
        secInsPay: secInsPay || null,
        secDiagnosis: secDiagnosis || '',
        patientPay: patientPay || null,
        quantity: quantity || null,
        daysSupply: daysSupply || '',
        directions: directions || '',
        copayApproval: copayApproval || '',
        copayNetwork: copayNetwork || '',
        homeCare: homeCare || false,
        hcHome: hcHome || null,
        hcPhone,
        doNotRefill,
        productsSelected: [Product],
        salesCode: salesCode || '',
      };

      // refillsData = utils.keysToCamelCase(sendData);
      refillsData = sendData;
    }

    try {
      if (refillsData) {
        if (addNewScript) {
          await api.addScript(refillsData);
        } else {
          await Promise.all([api.updateProduct(data), api.addScript(refillsData)]);
        }

        toast.success(`Added а new script with status "${newStatus}"`);
      } else {
        await api.updateProduct(data);
      }

      if (!addNewScript) {
        changeStatus({ status: 'Shipped' }, { Product: { ...Product, quantity: newProductQuantity } });
      }
    } catch (e) {
      toast.error('Something went wrong');
    }

    setRefillSaveLoading(false);
    setRefillModalInfo({});
  };

  const saveCancelStatus = (reason) => {
    changeStatus({ status: 'Cancelled', cancelReason: reason });

    cancelModalOpeningToggle(false);
  };

  const assignTo = async () => {
    assignToLoadingToggle(true);
    const { assignToValue, text } = assignBtnText.toLowerCase().includes('me')
      ? {
        assignToValue: 'one',
        text: `Assign to ${accountHelpers.getUser()?.name}`,
      }
      : assignBtnText.toLowerCase().includes('all')
        ? {
          assignToValue: 'none',
          text: 'Assign to me',
        }

        : {
          assignToValue: 'all',
          text: `Assign All to ${accountHelpers.getUser()?.name}`,
        };

    setAssignBtnText(text);

    await api.assignTo({ scriptId, assign: assignToValue });

    assignToLoadingToggle(false);
  };

  const tabs = useMemo(() => {
    let scriptTabs = [
      {
        name: 'Details',
        content: <Details
          info={scriptData}
          setRefillModalData={setRefillModalData}
        />,
        id: 'details-id',
      },
      {
        name: 'Notes',
        content: <Notes changeCount={changeTabCounts} />,
      },
      {
        name: 'Attachments',
        content: <Attachments changeCount={changeTabCounts} />,
      },
      {
        name: 'RX History',
        content: <RXHistory
          scriptStatus={status}
          scheduleIds={scriptData.scheduleIds}
          changeScriptInfo={changeScriptInfo}
          changeCounts={changeTabCounts}
        />,
        id: 'rx-history-id',
      },
      {
        name: 'Faxes',
        content: 'Faxes',
      },
      {
        name: 'Statuses',
        content: <Status />,
      },
      {
        name: 'Payments',
        content: <Payments />,
      },
    ];

    if (isPatient) {
      scriptTabs = scriptTabs.filter((t) => (t.name !== 'Attachments'
      && t.name !== 'Faxes'
      && t.name !== 'Statuses'));
    } else if (isHealth) {
      scriptTabs = scriptTabs.filter((t) => (
        t.name !== 'Faxes'
        && t.name !== 'Statuses'
      && t.name !== 'Payments'));
    }
    return scriptTabs;
  }, [scriptData]);

  const shippingLabelClick = useCallback(async () => {
    setShippingLoad(true);

    if (_.isEmpty(postalServicesData)) {
      toast.error('Choose someone postal services');
    } else {
      console.log(postalServicesData);
    }
    setShippingLoad(false);
  }, [postalServicesData]);

  return (
    <div className={classes.script_content}>
      {scriptLoading
        ? <Loader className={classes.script_loader} />

        : (
          <div className={classes.script_wrapper}>
            {updateLoading && <Loader className={classes.loader} />}

            {isAdmin && (
            <div className={classes.btns_wrapper}>
              <Button
                title={assignBtnText}
                buttonColor="#5F6C8E"
                reverseColor
                onClick={assignTo}
                loading={assignToLoading}
                disabled={assign && +assign !== +accountHelpers.getUser().id}
              />

              <Button
                title="Charge credit card"
                buttonColor="#5F6C8E"
                onClick={() => paymentModalToggle(true)}
              />

              <Button
                className={classes.edit_btn}
                title="Edit"
                editButton
                buttonColor="#5F6C8E"
                href={`/scripts/${scriptId}/edit`}
              />

              {status !== 'Cancelled' && (
              <Button
                className={classes.edit_btn}
                title="Cancel"
                clearButton
                onClick={() => cancelModalOpeningToggle(true)}
              />
              )}
            </div>
            )}

            {isPatient && (
              <div className={classes.btns_wrapper}>
                <Button
                  title="Pay with credit card"
                  buttonColor="#5F6C8E"
                  onClick={() => paymentModalToggle(true)}
                />
              </div>
            )}

            {(!isPatient && !isHealth) && (
            <ScriptsStatusBar
              onClick={() => {
              }}
              activeStatuses={[status]}
              unclickable
            />
            )}

            <div className={classes.status_wrapper}>
              <h2>
                Status:

                <StatusField status={status || ''} />
              </h2>

              {/* {scheduleDeliveryMethod === 'US Postal' */}
              {/*  && ( */}
              {/*  <Button */}
              {/*    title="Buy shipping label" */}
              {/*    reverseColor */}
              {/*    onClick={shippingLabelClick} */}
              {/*    loading={shippingLoad} */}
              {/*  /> */}
              {/*  )} */}

            </div>

            {isAdmin && (
            <div className={classes.btns_wrapper}>
              <StatusForm
                info={scriptData}
                currentTab={currentTab}
                changeStatus={changeStatus}
                changeScriptInfo={changeScriptInfo}
                setRefillModalData={setRefillModalData}
                openCancelModal={() => cancelModalOpeningToggle(true)}
                patientId={PatientId}
              />
            </div>
            )}

            <div className={classes.tab_wrapper}>
              <div className={classes.pouch_checkbox}>
                <p>{`Frequency Code ${frequencyCode}`}</p>

                {!isPatient && (
                  <Checkbox
                    label="Pouch"
                    checked={pouch}
                    onChange={() => changeStatus({ pouch: !pouch })}
                  />
                )}
              </div>

              <Tabs
                setCurrentTab={changeTab}
                tabs={tabs}
                currentTab={currentTab}
                tabCounts={tabCounts}
                defaultLinePosition={currentTab !== 'Notes' ? { left: 0, right: 565 } : { left: 75, right: 495 }}
              />
            </div>

            <CancelModal
              onClose={() => cancelModalOpeningToggle(false)}
              isOpen={isCancelModalOpen}
              currentCancelReason={cancelReason || ''}
              onSave={saveCancelStatus}
            />

            <AttentionModal
              onClose={() => setRefillModalInfo({})}
              isOpen={!_.isEmpty(refillModalInfo)}
              onSave={saveRefillStatus}
              title={refillModalInfo.title}
              text={refillModalInfo.text}
              loading={refillSaveLoading}
            />

            {isPaymentModalOpen && (
            <PaymentModal
              onClose={() => paymentModalToggle(false)}
              isOpen={isPaymentModalOpen}
              patientId={PatientId}
              setPaymentSuccessModalData={setPaymentSuccessModalData}
            />
            )}

            {!_.isEmpty(paymentSuccessModalData) && (
            <PaymentSuccessModal
              onClose={() => setPaymentSuccessModalData({})}
              paymentData={paymentSuccessModalData}
              scriptData={scriptData}
            />
            )}
          </div>
        )}
    </div>
  );
};

Script.propTypes = {

};

export default Script;
